import React, { createContext, useContext, useEffect, useState } from 'react';
import socketService, { Message } from '../services/socket';
import { useAuth } from './AuthContext';
import { Match } from '../types';
import toast from 'react-hot-toast';

// Socket context interface
interface SocketContextType {
  socket: WebSocket | null;
  isConnected: boolean;
  joinMatch: (matchId: string) => void;
  leaveMatch: (matchId: string) => void;
  sendMessage: (matchId: string, content: string, type?: string) => void;
  startTyping: (matchId: string) => void;
  stopTyping: (matchId: string) => void;
  onNewMessage: (callback: (message: Message) => void) => void;
  onUserTyping: (callback: (data: { userId: string; matchId: string; typing: boolean }) => void) => void;
  onMatchUpdate: (callback: (match: Match) => void) => void;
  removeListener: (event: string, callback?: Function) => void;
  connect: () => Promise<void>; // Add explicit connect method
  disconnect: () => void; // Add explicit disconnect method
}

// Create context
const SocketContext = createContext<SocketContextType | undefined>(undefined);

// Socket provider component
export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const { user, token } = useAuth();
  const [messageCallbacks, setMessageCallbacks] = useState<Array<(message: Message) => void>>([]);
  const [typingCallbacks, setTypingCallbacks] = useState<Array<(data: { userId: string; matchId: string; typing: boolean }) => void>>([]);
  const [matchCallbacks, setMatchCallbacks] = useState<Array<(match: Match) => void>>([]);

  // Initialize socket connection only when explicitly requested
  // Removed the automatic connection on user/token change

  // Join match conversation - this will trigger connection if needed
  const joinMatch = (matchId: string): void => {
    // DISABLED: Connect if not already connected - WebSockets should only be used for negotiation mode
    /*
    if (!isConnected) {
      connect()
        .then(() => {
          socketService.joinMatch(matchId);
          console.log(`👥 Joining match: ${matchId}`);
        })
        .catch((error) => {
          console.error('Failed to connect for match join:', error);
          toast.error('Failed to connect to messaging service');
        });
    } else {
      socketService.joinMatch(matchId);
      console.log(`👥 Joining match: ${matchId}`);
    }
    */
    // For now, we're not using WebSockets for regular chat
    console.log(`👥 Would join match: ${matchId} (WebSocket connection disabled)`);
  };

  // Leave match conversation
  const leaveMatch = (matchId: string): void => {
    // DISABLED: No WebSocket connection to leave
    /*
    if (isConnected) {
      socketService.leaveMatch(matchId);
      console.log(`👋 Leaving match: ${matchId}`);
    }
    */
    console.log(`👋 Would leave match: ${matchId} (WebSocket connection disabled)`);
  };

  // Send message
  const sendMessage = (matchId: string, content: string, type: string = 'TEXT'): void => {
    // DISABLED: No WebSocket connection to send messages
    /*
    if (isConnected) {
      socketService.sendMessage(matchId, content, type);
    } else {
      toast.error('Not connected to messaging service');
    }
    */
    console.log(`Would send message to match: ${matchId} (WebSocket connection disabled)`);
  };

  // Start typing indicator
  const startTyping = (matchId: string): void => {
    // DISABLED: No WebSocket connection for typing indicators
    /*
    if (isConnected) {
      socketService.startTyping(matchId);
    }
    */
    console.log(`Would start typing for match: ${matchId} (WebSocket connection disabled)`);
  };

  // Stop typing indicator
  const stopTyping = (matchId: string): void => {
    // DISABLED: No WebSocket connection for typing indicators
    /*
    if (isConnected) {
      socketService.stopTyping(matchId);
    }
    */
    console.log(`Would stop typing for match: ${matchId} (WebSocket connection disabled)`);
  };

  // Listen for new messages
  const onNewMessage = (callback: (message: Message) => void): void => {
    setMessageCallbacks(prev => [...prev, callback]);
  };

  // Listen for typing indicators
  const onUserTyping = (callback: (data: { userId: string; matchId: string; typing: boolean }) => void): void => {
    setTypingCallbacks(prev => [...prev, callback]);
  };

  // Listen for match updates
  const onMatchUpdate = (callback: (match: Match) => void): void => {
    setMatchCallbacks(prev => [...prev, callback]);
  };

  // Remove event listener
  const removeListener = (event: string, callback?: Function): void => {
    // In our simple implementation, we don't have a way to remove individual callbacks
    // This would need to be enhanced if needed
    console.log('removeListener called for event:', event);
  };

  // Explicit connect method - DISABLED for now
  const connect = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      // DISABLED: No automatic connections
      console.log('WebSocket connection is disabled for regular chat');
      resolve();
      /*
      // Only connect when we have both user and token
      if (user && token) {
        // Connect using our native WebSocket service
        socketService.connect()
          .then(() => {
            console.log('🔌 Connected to WebSocket server');
            setIsConnected(true);
            toast.success('Connected to real-time messaging');
            
            // Set up message listeners
            const unsubscribeMessage = socketService.onMessage('all', (message: Message) => {
              messageCallbacks.forEach(callback => {
                try {
                  callback(message);
                } catch (error) {
                  console.error('Error in message callback:', error);
                }
              });
            });

            const unsubscribeTyping = socketService.onTyping('all', (typing) => {
              typingCallbacks.forEach(callback => {
                try {
                  callback(typing);
                } catch (error) {
                  console.error('Error in typing callback:', error);
                }
              });
            });

            resolve();
          })
          .catch((error) => {
            console.error('Socket connection error:', error);
            setIsConnected(false);
            toast.error('Failed to connect to real-time messaging');
            reject(error);
          });
      } else {
        reject(new Error('User not authenticated'));
      }
      */
    });
  };

  // Explicit disconnect method - DISABLED for now
  const disconnect = (): void => {
    // DISABLED: No WebSocket connection to disconnect
    /*
    socketService.disconnect();
    setIsConnected(false);
    console.log('🔌 Disconnected from WebSocket server');
    */
    console.log('🔌 WebSocket disconnection disabled (no connection to disconnect)');
  };

  // Cleanup function
  useEffect(() => {
    // Cleanup on unmount - DISABLED
    /*
    return () => {
      console.log('🔌 Cleaning up socket connection');
      socketService.disconnect();
      setIsConnected(false);
    };
    */
    return () => {
      console.log('🔌 Socket cleanup disabled (no connection to clean up)');
    };
  }, []);

  // Context value
  const value: SocketContextType = {
    socket: null, // DISABLED: socketService.getSocket(),
    isConnected: false, // DISABLED: isConnected,
    joinMatch,
    leaveMatch,
    sendMessage,
    startTyping,
    stopTyping,
    onNewMessage,
    onUserTyping,
    onMatchUpdate,
    removeListener,
    connect, // Expose connect method
    disconnect // Expose disconnect method
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

// Custom hook to use socket context
export const useSocket = (): SocketContextType => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};